import React, { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import Button from "@mui/material/Button";
import { Link, Navigate } from "react-router";
import { getCaseLink, getProductData } from "@/src/services/productService";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchResource, queryKeys, updateResource } from "@/src/services/reactQuery/reactQueryService";
import useProductParams from "@/src/hooks/useProductParams";
import dayjs from "dayjs";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import { Stack } from "@mui/material";
import ScrollToTop from "@/src/services/ScrollToTop";

const RevokePage = () => {
  const [isChecked, setIsChecked] = useState(false);
  const [isLoadingInternal, setIsLoadingInternal] = useState(false);
  const { productId } = useProductParams();
  const productApiUri = getProductData("traffic", "apiUri");
  const { data: traffic, isLoading } = useQuery({
    queryKey: queryKeys.item(productApiUri, productId),
    queryFn: () => fetchResource(productApiUri, productId),
  });
  const queryClient = useQueryClient();
  const updateMutation = useMutation({
    mutationFn: updateResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, variables.id), data),
  });
  const confirmRevoke = async () => {
    setIsLoadingInternal(true);
    await updateMutation.mutateAsync({
      uri: "/traffic",
      id: traffic.id,
      data: {
        customerAppealRevocationApproved: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      },
    });
    setIsLoadingInternal(false);
  };

  if (isLoading) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  if (traffic.investigation.assessment !== "revoke") {
    return <Navigate to={getCaseLink(traffic)} />;
  }

  const customerApprovedDate = dayjs(traffic.customerAppealRevocationApproved).format("DD.MM.YYYY");
  const customerApprovedTime = dayjs(traffic.customerAppealRevocationApproved).format("HH:mm");

  if (!!traffic.customerAppealRevocationApproved) {
    return (
      <LayoutProtectedRoutes>
        <ScrollToTop />
        <ContentBox headline={<strong>Einspruchsrücknahme wurde bestätigt</strong>} headlineVariant={"h5"} showDivider={true}>
          <Typography sx={{ marginBottom: "1.5rem" }}>
            Wir haben Ihre Bestätigung der Rücknahme am{" "}
            <strong>
              {customerApprovedDate} {customerApprovedTime !== "00:00" && "um " + customerApprovedTime + " Uhr"}
            </strong>{" "}
            erhalten und leiten nun alle weiteren Schritte für Sie ein. Sie müssen lediglich der Zahlung der verhängten Buße nachkommen.
          </Typography>
          <Button variant={"outlined"} color={"primary"} component={Link} to={"/servicewelt"} fullWidth>
            Zu meinem Fall
          </Button>
        </ContentBox>
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      <ContentBox headline={<strong>Bestätigung der Einspruchsrücknahme</strong>} headlineVariant={"h5"} showDivider={true}>
        <Typography sx={{ marginBottom: "1.5rem" }}>
          Sobald der Einspruch zurückgenommen wurde, erhalten Sie hierzu eine entsprechende Bestätigung per E-Mail.
        </Typography>
        <Stack direction={"row"} sx={{ alignItems: "flex-start", marginBottom: "2rem" }} spacing={{ xs: 1, md: 2 }}>
          <Checkbox checked={isChecked} onChange={() => setIsChecked(!isChecked)} />
          <Typography sx={{ paddingTop: "0.25rem" }}>
            Ich wurde von den Legalbird Rechtsanwälten darüber aufgeklärt, dass die Abwehr der Buße schlechte Erfolgsaussichten hat. Mir ist bekannt, dass der
            Bußgeldbescheid somit rechtswirksam wird und die verhangene Buße innerhalb von 14 Tagen zu bezahlen ist.
          </Typography>
        </Stack>
        <ButtonLoading fullWidth variant={"contained"} disabled={!isChecked} isLoading={isLoadingInternal} color={"primary"} onClick={confirmRevoke}>
          Rücknahme bestätigen
        </ButtonLoading>
      </ContentBox>
    </LayoutProtectedRoutes>
  );
};

export default RevokePage;
